import React, { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AnimatePresence, motion } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowRight,
  faCarSide,
  faCheck,
  faCoins,
  faPhone,
  faPlane,
  faStethoscope,
  faWallet
} from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import Banner from './Banner';
import { sendWebsiteEmail } from '../api';
import pricingRates from '../data/pricing.json';
import '../styles/Reservation.css';

const createEmptyForm = () => ({
  type: 'reservation',
  transportType: 'medical',
  outsideBrussels: false,
  roundTrip: false,
  distanceKm: '',
  waitingQuarters: '0',
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  date: '',
  time: '',
  pickupAddress: '',
  dropoffAddress: '',
  passengers: '1',
  mobility: '',
  comments: '',
  motif: '',
  needAccompanist: '',
  message: '',
  website: ''
});

const pageCopy = {
  fr: {
    progress: ['Le trajet', 'Le patient', 'Vos besoins', 'Récapitulatif'],
    tripTitle: 'Votre trajet', typeLabel: 'Type de transport',
    types: {
      medical: ['Médico-sanitaire', 'Hôpital, dialyse, consultation, transfert'],
      nonMedical: ['Non médico-sanitaire', 'Démarches, courses, sorties essentielles'],
      airport: ['Aéroport', 'Vers Bruxelles-National, forfait de 47 €']
    },
    pickup: 'Adresse de prise en charge', pickupPlaceholder: 'Rue, numéro, commune',
    destination: 'Adresse de destination', destinationPlaceholder: 'Hôpital, cabinet, adresse…',
    date: 'Date du transport', time: 'Heure de prise en charge',
    outside: 'Le départ ou l’arrivée est hors de la Région de Bruxelles-Capitale',
    distance: 'Distance estimée pour un trajet simple', distanceHint: 'Indiquez la distance routière approximative.',
    roundTrip: 'Aller-retour', waiting: 'Attente ou accompagnement (à confirmer)',
    noWaiting: 'Aucune attente', quarter: 'quart d’heure', quarters: 'quarts d’heure',
    estimateTitle: 'Estimation indicative', live: 'Mise à jour en temps réel',
    base: 'Tarif de base', distanceExtras: 'Kilomètres supplémentaires', returnTrip: 'Trajet retour', nightRate: 'Tarif de nuit (20 h - 6 h)',
    nonMedicalRate: 'Tarif non médico-sanitaire', nonMedicalOutsideRate: 'Hors Bruxelles (2,50 €/km, minimum 45 €)', airportRate: 'Forfait aéroport',
    total: 'Total estimé', deposit: 'Acompte de 30 % à la confirmation',
    disclaimer: 'Estimation calculée selon le type de transport sélectionné. Le prix définitif vous est confirmé par notre équipe.',
    help: 'Besoin d’aide pour réserver ?', reimbursement: 'Votre mutualité ou le CPAS peuvent rembourser une partie du transport.',
    next: 'Continuer', previous: 'Précédent',
    medical: 'Transport médico-sanitaire', nonMedical: 'Transport non médico-sanitaire', airport: 'Transport aéroport',
    transportType: 'Type de transport', outsideLabel: 'Hors Bruxelles', distanceLabel: 'Distance', yes: 'Oui', no: 'Non'
  },
  nl: {
    progress: ['De rit', 'De patiënt', 'Uw behoeften', 'Overzicht'],
    tripTitle: 'Uw rit', typeLabel: 'Type vervoer',
    types: {
      medical: ['Medisch-sanitair', 'Ziekenhuis, dialyse, raadpleging, transfer'],
      nonMedical: ['Niet medisch-sanitair', 'Afspraken, boodschappen, essentiële ritten'],
      airport: ['Luchthaven', 'Naar Brussels Airport, forfait van 47 €']
    },
    pickup: 'Ophaaladres', pickupPlaceholder: 'Straat, nummer, gemeente',
    destination: 'Bestemmingsadres', destinationPlaceholder: 'Ziekenhuis, praktijk, adres…',
    date: 'Datum van het vervoer', time: 'Ophaaluur',
    outside: 'Het vertrek of de aankomst ligt buiten het Brussels Hoofdstedelijk Gewest',
    distance: 'Geschatte afstand voor een enkele rit', distanceHint: 'Vul de geschatte afstand over de weg in.',
    roundTrip: 'Heen en terug', waiting: 'Wachten of begeleiding (te bevestigen)',
    noWaiting: 'Geen wachttijd', quarter: 'kwartier', quarters: 'kwartieren',
    estimateTitle: 'Indicatieve raming', live: 'Wordt meteen bijgewerkt',
    base: 'Basistarief', distanceExtras: 'Bijkomende kilometers', returnTrip: 'Terugrit', nightRate: 'Nachttarief (20 u - 6 u)',
    nonMedicalRate: 'Niet-medisch tarief', nonMedicalOutsideRate: 'Buiten Brussel (2,50 €/km, minimum 45 €)', airportRate: 'Luchthavenforfait',
    total: 'Geschat totaal', deposit: 'Voorschot van 30 % bij bevestiging',
    disclaimer: 'Raming volgens het gekozen vervoerstype. Ons team bevestigt de definitieve prijs.',
    help: 'Hulp nodig bij uw reservatie?', reimbursement: 'Uw mutualiteit of het OCMW kan een deel van het vervoer terugbetalen.',
    next: 'Doorgaan', previous: 'Vorige',
    medical: 'Medisch-sanitair vervoer', nonMedical: 'Niet medisch-sanitair vervoer', airport: 'Luchthavenvervoer',
    transportType: 'Type vervoer', outsideLabel: 'Buiten Brussel', distanceLabel: 'Afstand', yes: 'Ja', no: 'Nee'
  }
};

export function calculateEstimate(data) {
  const distance = Math.max(0, Number(data.distanceKm) || 0);
  const hour = Number.parseInt(String(data.time || '').split(':')[0], 10);
  const isNight = data.transportType === 'medical' && Number.isInteger(hour)
    && (hour >= pricingRates.nightStartsAt || hour < pricingRates.nightEndsAt);
  let pricingMode = 'medical';
  let baseRate = isNight ? pricingRates.nightRate : pricingRates.baseRate;
  let distanceCharge = 0;

  if (data.transportType === 'airport') {
    pricingMode = 'airport';
    baseRate = pricingRates.airportRate;
  } else if (data.transportType === 'nonMedical') {
    pricingMode = data.outsideBrussels ? 'nonMedicalOutside' : 'nonMedical';
    baseRate = data.outsideBrussels
      ? Math.max(pricingRates.nonMedicalOutsideMinimum, distance * pricingRates.nonMedicalOutsideRatePerKm)
      : pricingRates.nonMedicalRate;
  } else if (data.outsideBrussels) {
    const fromEleven = Math.max(
      0,
      Math.min(distance, pricingRates.secondTierEndsAtKm) - pricingRates.includedKm
    ) * pricingRates.kmRate11To30;
    const fromThirtyOne = Math.max(
      0,
      distance - pricingRates.secondTierEndsAtKm
    ) * pricingRates.kmRateFrom31;
    distanceCharge = Math.round((fromEleven + fromThirtyOne) * 100) / 100;
  }

  baseRate = Math.round(baseRate * 100) / 100;
  const oneWay = Math.round((baseRate + distanceCharge) * 100) / 100;
  const tripPrice = Math.round(oneWay * (data.roundTrip ? 2 : 1) * 100) / 100;
  const total = tripPrice;

  return {
    baseRate,
    distanceCharge,
    oneWay,
    tripPrice,
    total,
    deposit: Math.round(total * pricingRates.depositRate * 100) / 100,
    isNight,
    pricingMode
  };
}

function Reservation() {
  const { t, i18n } = useTranslation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [formErrors, setFormErrors] = useState({});
  const [formData, setFormData] = useState(createEmptyForm);
  const formStartedAt = useRef(Date.now());
  const lang = i18n.resolvedLanguage?.startsWith('nl') ? 'nl' : 'fr';
  const c = pageCopy[lang];
  const estimate = useMemo(() => calculateEstimate(formData), [formData]);
  const currency = (value) => new Intl.NumberFormat(lang === 'fr' ? 'fr-BE' : 'nl-BE', {
    style: 'currency', currency: 'EUR', minimumFractionDigits: 2
  }).format(value);
  const transportLabel = c[formData.transportType] || c.medical;

  const validateEmail = (email) => /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(email);
  const validatePhone = (phone) => /^(?:(?:\+|00)32|0)(?:\d{9}|\d{8})$/.test(phone.replace(/\s/g, ''));

  const validateField = (name, value) => {
    let error = '';
    if ((name === 'firstName' || name === 'lastName') && value.length < 2) {
      error = t('reservation.errors.minLength', { field: t(`reservation.steps.personal.${name}`), min: 2 });
    } else if (name === 'email' && !validateEmail(value)) {
      error = t('reservation.errors.invalidEmail');
    } else if (name === 'phone' && !validatePhone(value)) {
      error = t('reservation.errors.invalidPhone');
    } else if (name === 'date' && value) {
      const selectedDate = new Date(`${value}T12:00:00`);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (selectedDate < today) error = t('reservation.errors.pastDate');
    } else if (name === 'passengers') {
      const count = Number(value);
      if (!Number.isInteger(count) || count < 1 || count > 4) error = t('reservation.errors.invalidPassengers');
    }
    return error;
  };

  const handleChange = (event) => {
    const { name, type, checked, value } = event.target;
    const nextValue = type === 'checkbox' ? checked : value;
    setFormData((previous) => ({ ...previous, [name]: nextValue }));
    if (type !== 'checkbox') {
      setFormErrors((previous) => ({ ...previous, [name]: validateField(name, value) }));
    }
  };

  const selectTransport = (transportType) => {
    setFormData((previous) => ({
      ...previous,
      transportType,
      outsideBrussels: transportType === 'airport' ? false : previous.outsideBrussels,
      distanceKm: transportType === 'airport' ? '' : previous.distanceKm
    }));
  };

  const isStepValid = (step) => {
    if (step === 1) {
      const distanceValid = !formData.outsideBrussels || Number(formData.distanceKm) > 0;
      return formData.date && formData.time && formData.pickupAddress && formData.dropoffAddress && distanceValid && !formErrors.date;
    }
    if (step === 2) {
      return formData.firstName && formData.lastName && formData.email && formData.phone
        && !formErrors.firstName && !formErrors.lastName && !formErrors.email && !formErrors.phone;
    }
    if (step === 3) {
      return formData.passengers && formData.mobility && formData.motif && formData.needAccompanist && !formErrors.passengers;
    }
    return true;
  };

  const nextStep = () => {
    if (isStepValid(currentStep)) setCurrentStep((step) => Math.min(step + 1, 4));
    else toast.error(t('reservation.error.required'));
  };
  const prevStep = () => setCurrentStep((step) => Math.max(step - 1, 1));

  const sendEmail = async () => {
    try {
      setIsSubmitting(true);
      await sendWebsiteEmail({
        ...formData,
        formDurationMs: Date.now() - formStartedAt.current,
        estimatedPrice: estimate.total.toFixed(2),
        estimatedDeposit: estimate.deposit.toFixed(2)
      });
      toast.success(t('reservation.success'));
      setFormData(createEmptyForm());
      formStartedAt.current = Date.now();
      setCurrentStep(1);
    } catch (error) {
      console.error('Erreur lors de l’envoi de l’email :', error);
      toast.error(t('reservation.error'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderProgressBar = () => (
    <div className="reservation-progress" aria-label={lang === 'fr' ? 'Étapes de réservation' : 'Reservatiestappen'}>
      {c.progress.map((label, index) => {
        const step = index + 1;
        return <div key={label} className={currentStep >= step ? 'active' : ''}><span>{currentStep > step ? <FontAwesomeIcon icon={faCheck} /> : step}</span><small>{label}</small></div>;
      })}
    </div>
  );

  const renderTripDetails = () => (
    <motion.div key="trip" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="form-step reservation-trip-step">
      <h3>{c.tripTitle}</h3>
      <fieldset className="transport-type-fieldset">
        <legend>{c.typeLabel}</legend>
        <div className="transport-type-grid">
          {[
            ['medical', faStethoscope],
            ['nonMedical', faCarSide],
            ['airport', faPlane]
          ].map(([value, icon]) => (
            <label key={value} className={formData.transportType === value ? 'selected' : ''}>
              <input type="radio" name="transportType" value={value} checked={formData.transportType === value} onChange={() => selectTransport(value)} />
              <FontAwesomeIcon icon={icon} /><span><strong>{c.types[value][0]}</strong><small>{c.types[value][1]}</small></span>
            </label>
          ))}
        </div>
      </fieldset>

      <div className="trip-fields-grid">
        <div><label htmlFor="pickupAddress">{c.pickup}</label><input id="pickupAddress" name="pickupAddress" value={formData.pickupAddress} onChange={handleChange} placeholder={c.pickupPlaceholder} required /></div>
        <div><label htmlFor="dropoffAddress">{c.destination}</label><input id="dropoffAddress" name="dropoffAddress" value={formData.dropoffAddress} onChange={handleChange} placeholder={c.destinationPlaceholder} required /></div>
        <div><label htmlFor="date">{c.date}</label><input type="date" id="date" name="date" value={formData.date} onChange={handleChange} className={formErrors.date ? 'error' : ''} required />{formErrors.date && <span className="error-message">{formErrors.date}</span>}</div>
        <div><label htmlFor="time">{c.time}</label><input type="time" id="time" name="time" value={formData.time} onChange={handleChange} required /></div>
      </div>

      {formData.transportType !== 'airport' && <label className="reservation-toggle"><input type="checkbox" name="outsideBrussels" checked={formData.outsideBrussels} onChange={handleChange} /><span aria-hidden="true"></span><strong>{c.outside}</strong></label>}
      {formData.outsideBrussels && <div className="distance-field"><label htmlFor="distanceKm">{c.distance}</label><div><input type="number" min="1" max="500" step="1" id="distanceKm" name="distanceKm" value={formData.distanceKm} onChange={handleChange} placeholder="25" required /><span>km</span></div><small>{c.distanceHint}</small></div>}

      <div className="trip-options-grid">
        <label className="reservation-toggle"><input type="checkbox" name="roundTrip" checked={formData.roundTrip} onChange={handleChange} /><span aria-hidden="true"></span><strong>{c.roundTrip}</strong></label>
        <div className="waiting-field"><label htmlFor="waitingQuarters">{c.waiting}</label><select id="waitingQuarters" name="waitingQuarters" value={formData.waitingQuarters} onChange={handleChange}><option value="0">{c.noWaiting}</option>{[1, 2, 3, 4, 5, 6, 7, 8].map((quarter) => <option key={quarter} value={quarter}>{quarter} {quarter === 1 ? c.quarter : c.quarters}</option>)}</select></div>
      </div>
    </motion.div>
  );

  const renderPersonalInfo = () => (
    <motion.div key="personal" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="form-step">
      <h3>{t('reservation.steps.personal.title')}</h3>
      <div className="form-group">
        <div><label htmlFor="firstName">{t('reservation.steps.personal.firstName')}</label><input id="firstName" name="firstName" value={formData.firstName} onChange={handleChange} required minLength="2" className={formErrors.firstName ? 'error' : ''} />{formErrors.firstName && <span className="error-message">{formErrors.firstName}</span>}</div>
        <div><label htmlFor="lastName">{t('reservation.steps.personal.lastName')}</label><input id="lastName" name="lastName" value={formData.lastName} onChange={handleChange} required minLength="2" className={formErrors.lastName ? 'error' : ''} />{formErrors.lastName && <span className="error-message">{formErrors.lastName}</span>}</div>
      </div>
      <div className="form-group">
        <div><label htmlFor="email">{t('reservation.steps.personal.email')}</label><input type="email" id="email" name="email" value={formData.email} onChange={handleChange} required className={formErrors.email ? 'error' : ''} />{formErrors.email && <span className="error-message">{formErrors.email}</span>}</div>
        <div><label htmlFor="phone">{t('reservation.steps.personal.phone')}</label><input type="tel" id="phone" name="phone" value={formData.phone} onChange={handleChange} required placeholder="+32 XXX XX XX XX" className={formErrors.phone ? 'error' : ''} />{formErrors.phone && <span className="error-message">{formErrors.phone}</span>}</div>
      </div>
    </motion.div>
  );

  const renderMobilityNeeds = () => (
    <motion.div key="mobility" className="form-step" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
      <h3>{t('reservation.steps.mobility.title')}</h3>
      <div className="form-group">
        <div><label htmlFor="passengers">{t('reservation.steps.mobility.passengers')}</label><select id="passengers" name="passengers" value={formData.passengers} onChange={handleChange} required>{[1, 2, 3, 4].map((count) => <option key={count} value={count}>{count}</option>)}</select></div>
        <div><label htmlFor="mobility">{t('reservation.steps.mobility.select')}</label><select id="mobility" name="mobility" value={formData.mobility} onChange={handleChange} required><option value="">{t('reservation.steps.mobility.select')}</option><option value={t('reservation.steps.mobility.wheelchair')}>{t('reservation.steps.mobility.wheelchair')}</option><option value={t('reservation.steps.mobility.walker')}>{t('reservation.steps.mobility.walker')}</option><option value={t('reservation.steps.mobility.assistance')}>{t('reservation.steps.mobility.assistance')}</option><option value={t('reservation.steps.mobility.none')}>{t('reservation.steps.mobility.none')}</option></select></div>
      </div>
      <div className="form-group">
        <div><label htmlFor="motif">{t('reservation.steps.mobility.motifSelect')}</label><select id="motif" name="motif" value={formData.motif} onChange={handleChange} required><option value="">{t('reservation.steps.mobility.motifSelect')}</option><option value={t('reservation.steps.motif.dentist')}>{t('reservation.steps.motif.dentist')}</option><option value={t('reservation.steps.motif.cardio')}>{t('reservation.steps.motif.cardio')}</option><option value={t('reservation.steps.motif.chirurgie')}>{t('reservation.steps.motif.chirurgie')}</option><option value={t('reservation.steps.motif.radiologie')}>{t('reservation.steps.motif.radiologie')}</option></select></div>
        <div><label htmlFor="needAccompanist">{t('reservation.steps.mobility.accompanist.label')}</label><select id="needAccompanist" name="needAccompanist" value={formData.needAccompanist} onChange={handleChange} required><option value="">{t('reservation.steps.mobility.accompanist.select')}</option><option value="oui">{t('reservation.steps.mobility.accompanist.yes')}</option><option value="non">{t('reservation.steps.mobility.accompanist.no')}</option></select></div>
      </div>
      <div className="form-group"><div className="full-width"><label htmlFor="comments">{t('reservation.steps.confirmation.comments')}</label><textarea id="comments" name="comments" value={formData.comments} onChange={handleChange} rows="4" placeholder={t('reservation.steps.confirmation.commentsPlaceholder')} /></div></div>
    </motion.div>
  );

  const renderConfirmation = () => (
    <motion.div key="confirmation" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="form-step">
      <h3>{t('reservation.steps.confirmation.title')}</h3>
      <div className="confirmation-details">
        <p><strong>{t('reservation.steps.confirmation.name')}:</strong> {formData.firstName} {formData.lastName}</p>
        <p><strong>{c.transportType}:</strong> {transportLabel}</p>
        <p><strong>{t('reservation.steps.confirmation.datetime')}:</strong> {formData.date} {t('translate.reservation_confirmation_at')} {formData.time}</p>
        <p><strong>{t('reservation.steps.confirmation.pickup')}:</strong> {formData.pickupAddress}</p>
        <p><strong>{t('reservation.steps.confirmation.dropoff')}:</strong> {formData.dropoffAddress}</p>
        <p><strong>{c.roundTrip}:</strong> {formData.roundTrip ? c.yes : c.no}</p>
        {formData.outsideBrussels && <p><strong>{c.distanceLabel}:</strong> {formData.distanceKm} km</p>}
        <p><strong>{t('reservation.steps.confirmation.passengers')}:</strong> {formData.passengers}</p>
        <p><strong>{t('reservation.steps.confirmation.mobility')}:</strong> {formData.mobility}</p>
        <p><strong>{t('reservation.steps.confirmation.motif')}:</strong> {formData.motif}</p>
        <p className="confirmation-estimate"><strong>{c.total}:</strong> {currency(estimate.total)}</p>
      </div>
      <div className="form-buttons"><button type="button" onClick={prevStep} className="prev-button">{c.previous}</button><button type="submit" className="submit-button" disabled={isSubmitting}>{isSubmitting ? t('reservation.buttons.sending') : t('reservation.buttons.submit')}</button></div>
    </motion.div>
  );

  const handleSubmit = (event) => {
    event.preventDefault();
    sendEmail();
  };

  return (
    <div className="reservation-container">
      <Banner
        title={t('reservation.title')}
        accent={lang === 'fr' ? 'transport' : 'vervoer'}
        subtitle={t('reservation.subtitle')}
      />
      <div className="reservation-layout">
        <div className="reservation-form-container">
          {renderProgressBar()}
          <form onSubmit={handleSubmit} className="reservation-form">
            <div className="sr-only" aria-hidden="true">
              <label htmlFor="reservation-website">Ne pas remplir ce champ</label>
              <input id="reservation-website" name="website" value={formData.website} onChange={handleChange} tabIndex="-1" autoComplete="off" />
            </div>
            <AnimatePresence mode="wait">
              {currentStep === 1 && renderTripDetails()}
              {currentStep === 2 && renderPersonalInfo()}
              {currentStep === 3 && renderMobilityNeeds()}
              {currentStep === 4 && renderConfirmation()}
            </AnimatePresence>
            {currentStep < 4 && <div className="form-buttons">{currentStep > 1 && <button type="button" onClick={prevStep} className="prev-button">{c.previous}</button>}<button type="button" onClick={nextStep} className="next-button">{c.next}<FontAwesomeIcon icon={faArrowRight} /></button></div>}
          </form>
        </div>

        <aside className="reservation-sidebar">
          <div className="estimate-card">
            <div className="estimate-heading"><FontAwesomeIcon icon={faCoins} /><div><h2>{c.estimateTitle}</h2><p>{c.live}</p></div></div>
            <div className="estimate-rate"><span>{transportLabel}<small>{formData.outsideBrussels ? c.outsideLabel : 'Bruxelles'}</small></span><strong>{currency(estimate.oneWay)}</strong></div>
            <div className="estimate-breakdown">
              <div><span>{estimate.isNight
                ? c.nightRate
                : estimate.pricingMode === 'airport'
                  ? c.airportRate
                  : estimate.pricingMode === 'nonMedicalOutside'
                    ? c.nonMedicalOutsideRate
                    : estimate.pricingMode === 'nonMedical'
                      ? c.nonMedicalRate
                      : c.base}</span><strong>{currency(estimate.baseRate)}</strong></div>
              {estimate.distanceCharge > 0 && <div><span>{c.distanceExtras}</span><strong>+ {currency(estimate.distanceCharge)}</strong></div>}
              {formData.roundTrip && <div><span>{c.returnTrip}</span><strong>+ {currency(estimate.oneWay)}</strong></div>}
            </div>
            <div className="estimate-total"><span>{c.total}</span><strong>{currency(estimate.total)}</strong></div>
            <p className="estimate-deposit"><FontAwesomeIcon icon={faWallet} />{c.deposit}: <strong>{currency(estimate.deposit)}</strong></p>
            <small className="estimate-disclaimer">{c.disclaimer}</small>
          </div>
          <div className="reservation-help-card"><h2>{c.help}</h2><a href="tel:+32491333777"><FontAwesomeIcon icon={faPhone} />+32 491 33 37 77</a><div><span>{lang === 'fr' ? 'Lundi – vendredi' : 'Maandag – vrijdag'}</span><strong>7h00 – 17h30</strong></div><div><span>{lang === 'fr' ? 'Samedi – dimanche' : 'Zaterdag – zondag'}</span><strong>9h00 – 16h00</strong></div><p><FontAwesomeIcon icon={faWallet} />{c.reimbursement}</p></div>
        </aside>
      </div>
    </div>
  );
}

export default Reservation;
